import { pilotVariantLabel, pilotVariantKeys, pilotPeople } from "@/lib/pilot";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { ChevronDown, Clock, Flag, Maximize2, Minus, Play, Plus, Radio, Reply, Users } from "lucide-react";
import type { CampaignDetail, CampaignStep, EmailMetrics } from "@/api/email";
import { Hint } from "@/components/email/EmailBadges";
import { StepBadges, StepFacts, StepMenu, SubjectLine, type StepBoardProps } from "@/components/email/StepBoard";
import { TemplateText } from "@/components/email/TemplateText";
import { delayLabel, plural, stepDays } from "@/lib/email";
import { cn } from "@/lib/utils";

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.6;
const PAD = 24;
const GRID = 20;
const STEP = 1.2;

interface View {
  x: number;
  y: number;
  k: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** A drag that starts on a node or a control is theirs, not a pan. */
function startsOnControl(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("[data-canvas-node], button, a, input, textarea");
}

/**
 * The sequence as a flow on a board that moves and zooms: Start, each email
 * with its delay and what it did, the exit a reply takes, then the end. Same
 * data and actions as the Steps view.
 */
export function StepCanvas(props: StepBoardProps) {
  const { campaign, canEdit } = props;
  const pilot = !!campaign.execution_lock && !!campaign.pilot?.editorial;
  const steps = campaign.steps;
  const days = stepDays(steps);
  const sequence = campaign.sequence;
  const canMove = canEdit && sequence.can_reorder_steps && steps.length > 1;
  // Between two steps: a "+" where the sequence takes an insert, a dimmed one
  // saying why where it does not (Lemlist, once contacts are in).
  const insertReason = canEdit && !sequence.can_insert_step ? (sequence.reasons.can_insert_step ?? null) : null;

  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointer: number; px: number; py: number; x: number; y: number } | null>(null);
  const [view, setView] = useState<View>({ x: 0, y: PAD, k: 1 });
  const [dragging, setDragging] = useState(false);

  const zoomAt = useCallback((cx: number, cy: number, factor: number) => {
    setView((v) => {
      const k = clamp(v.k * factor, MIN_ZOOM, MAX_ZOOM);
      if (k === v.k) return v;
      // The point under (cx, cy) stays under it.
      return { k, x: cx - ((cx - v.x) / v.k) * k, y: cy - ((cy - v.y) / v.k) * k };
    });
  }, []);

  const zoomFromCenter = useCallback(
    (factor: number) => {
      const el = viewportRef.current;
      if (el) zoomAt(el.clientWidth / 2, el.clientHeight / 2, factor);
    },
    [zoomAt],
  );

  /** "width": the flow's full width, top aligned, never above 100% — the
   *  first look, readable even when the sequence is long. "all": the whole
   *  flow as far as the minimum zoom allows. */
  const fit = useCallback((mode: "width" | "all") => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content || !content.offsetWidth) return;
    const [vw, vh] = [viewport.clientWidth, viewport.clientHeight];
    const [cw, ch] = [content.offsetWidth, content.offsetHeight];
    const byWidth = (vw - 2 * PAD) / cw;
    const k = clamp(Math.min(1, byWidth, mode === "all" ? (vh - 2 * PAD) / ch : 1), MIN_ZOOM, MAX_ZOOM);
    setView({ k, x: (vw - cw * k) / 2, y: mode === "all" ? Math.max(PAD, (vh - ch * k) / 2) : PAD });
  }, []);

  useLayoutEffect(() => {
    fit(pilot ? "all" : "width");
  }, [fit, pilot]);

  // A native listener: React's wheel handler is passive and could not keep
  // the page from scrolling while the board zooms.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      // Lines (Firefox) become pixels; a pinch (ctrl + wheel) sends small deltas.
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : 1);
      zoomAt(event.clientX - rect.left, event.clientY - rect.top, Math.exp(-delta * (event.ctrlKey ? 0.01 : 0.0015)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    // A step's menu is portaled out of the board, yet its events bubble here through React.
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (event.button !== 0 || startsOnControl(event.target)) return;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointer: event.pointerId, px: event.clientX, py: event.clientY, x: view.x, y: view.y };
    setDragging(true);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const start = drag.current;
    if (!start || start.pointer !== event.pointerId) return;
    setView((v) => ({ ...v, x: start.x + event.clientX - start.px, y: start.y + event.clientY - start.py }));
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
  }

  function onBoardKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    const pan: Record<string, [number, number]> = {
      ArrowLeft: [48, 0],
      ArrowRight: [-48, 0],
      ArrowUp: [0, 48],
      ArrowDown: [0, -48],
    };
    if (pan[event.key]) {
      const [dx, dy] = pan[event.key];
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    } else if (event.key === "+" || event.key === "=") zoomFromCenter(STEP);
    else if (event.key === "-") zoomFromCenter(1 / STEP);
    else if (event.key === "0") fit("all");
    else return;
    event.preventDefault();
  }

  function move(index: number, offset: -1 | 1): (() => void) | null {
    const target = index + offset;
    if (!canMove || target < 0 || target >= steps.length) return null;
    return () => {
      const ids = steps.map((s) => s.id);
      [ids[index], ids[target]] = [ids[target], ids[index]];
      props.onReorder(ids);
    };
  }

  return (
    <div className="space-y-2">
      <div
        ref={viewportRef}
        role="region"
        aria-label="Sequence canvas"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onBoardKey}
        className={cn(
          "relative overflow-hidden rounded-plane bg-slate-50/80 shadow-plane ring-1 ring-slate-900/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-indigo-300",
          pilot ? "h-[calc(100dvh-380px)] min-h-[350px]" : "h-[70vh] min-h-[520px]",
          dragging ? "cursor-grabbing select-none" : "cursor-grab",
        )}
        style={{
          touchAction: "none",
          backgroundImage: "radial-gradient(circle, rgb(203 213 225) 1px, transparent 1.3px)",
          backgroundSize: `${GRID * view.k}px ${GRID * view.k}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
      >
        <div
          ref={contentRef}
          className="absolute left-0 top-0 w-[760px] origin-top-left pb-6"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
        >
          {!pilot && <div className="flex justify-center"><StartNode campaign={campaign} /></div>}
          {steps.map((step, index) => (
            <Fragment key={step.id}>
              {(!pilot || index > 0) && <Connector compact={pilot}
                label={
                  index === 0
                    ? (pilot ? "J0" : `Day ${days[0]}`)
                    : pilot ? `J+${days[index]}` : step.delay_value === 0
                      ? "Same day"
                      : `+${delayLabel(step.delay_value, step.delay_unit)}`
                }
                onInsert={canEdit && sequence.can_insert_step ? () => props.onAdd(step.position) : null}
                insertLabel={`Insert a step before step ${step.position}`}
                insertReason={index > 0 ? insertReason : null}
              />}
              <div className="grid grid-cols-[200px_360px_200px] items-center">
                <span />
                <StepNode
                  step={step}
                  day={days[index]}
                  props={props}
                  metrics={campaign.metrics_by_step?.[String(step.position)]}
                  atStep={campaign.contacts_by_step[String(step.position)] ?? 0}
                  move={canMove ? { up: move(index, -1), down: move(index, 1) } : undefined}
                />
                {!pilot && <ExitBranch metrics={campaign.metrics_by_step?.[String(step.position)]} />}
              </div>
            </Fragment>
          ))}
          {canEdit && (
            <>
              <Connector dashed />
              <div className="flex justify-center">
                <Hint content={sequence.can_add_step ? null : sequence.reasons.can_add_step}>
                  <span className="block w-[360px]">
                    <button
                      type="button"
                      disabled={!sequence.can_add_step}
                      onClick={() => props.onAdd()}
                      className="flex w-full items-center justify-center gap-2 rounded-plane border-2 border-dashed border-slate-200 bg-white/70 px-4 py-3.5 text-sm font-medium text-slate-500 transition-colors enabled:hover:border-indigo-300 enabled:hover:bg-indigo-50/60 enabled:hover:text-indigo-600 disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />
                      {steps.length ? "Add a follow-up" : "Add the first email"}
                    </button>
                  </span>
                </Hint>
              </div>
            </>
          )}
          {!pilot && <><Connector dashed={canEdit} /><div className="flex justify-center"><EndNode campaign={campaign} /></div></>}
        </div>

        {!pilot && sequence.active_sequence && (
          <Hint content={sequence.message}>
            <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 shadow-sm-tile ring-1 ring-emerald-100">
              <Radio className="h-3 w-3" />
              Active sequence
            </span>
          </Hint>
        )}
        <div className="absolute right-3 top-3 z-10 flex items-center rounded-lg bg-white p-0.5 shadow-sm-tile ring-1 ring-slate-900/[0.06]">
          <ZoomButton label="Zoom out" disabled={view.k <= MIN_ZOOM} onClick={() => zoomFromCenter(1 / STEP)}>
            <Minus className="h-3.5 w-3.5" />
          </ZoomButton>
          <ZoomButton label="Reset the zoom to 100%" onClick={() => zoomFromCenter(1 / view.k)} wide>
            {Math.round(view.k * 100)}%
          </ZoomButton>
          <ZoomButton label="Zoom in" disabled={view.k >= MAX_ZOOM} onClick={() => zoomFromCenter(STEP)}>
            <Plus className="h-3.5 w-3.5" />
          </ZoomButton>
          <span className="mx-0.5 h-4 w-px bg-slate-200" />
          <ZoomButton label="Fit the whole sequence" onClick={() => fit("all")} wide>
            <Maximize2 className="h-3.5 w-3.5" />
            Fit
          </ZoomButton>
        </div>
      </div>
      <p className="text-center text-xs text-slate-400">
        {pilot ? "Glisser pour déplacer · défiler pour zoomer · cliquer pour ouvrir un email" : <>Drag to move · scroll to zoom{canEdit ? " · click a step to edit" : ""}</>}
      </p>
    </div>
  );
}

function ZoomButton({
  label,
  onClick,
  disabled,
  wide,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center justify-center gap-1 rounded-md text-xs font-medium tabular-nums text-slate-600 transition-colors enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:opacity-40",
        wide ? "min-w-[3rem] px-2" : "w-7",
      )}
    >
      {children}
    </button>
  );
}

/** The link between two nodes: the delay, and where a step can be inserted —
 *  or why it cannot be. */
function Connector({
  label,
  compact = false,
  onInsert,
  insertLabel,
  insertReason = null,
  dashed = false,
}: {
  label?: string;
  compact?: boolean;
  onInsert?: (() => void) | null;
  insertLabel?: string;
  insertReason?: string | null;
  dashed?: boolean;
}) {
  const line = dashed ? "border-l border-dashed border-slate-300" : "border-l border-slate-300";
  return (
    <div className="flex flex-col items-center">
      <span className={cn(compact ? "h-1" : "h-4", line)} />
      {label && (
        <div className="relative">
          <span
            data-canvas-node
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-500 shadow-sm-tile"
          >
            <Clock className="h-3 w-3" />
            {label}
          </span>
          {!onInsert && insertReason && (
            <Hint content={insertReason}>
              <span
                data-canvas-node
                aria-label={insertReason}
                className="absolute left-full top-1/2 ml-1.5 flex h-5 w-5 -translate-y-1/2 cursor-not-allowed items-center justify-center rounded-full border border-dashed border-slate-300 bg-white text-slate-300"
              >
                <Plus className="h-3 w-3" />
              </span>
            </Hint>
          )}
          {onInsert && (
            <button
              type="button"
              aria-label={insertLabel}
              title={insertLabel}
              onClick={onInsert}
              className="absolute left-full top-1/2 ml-1.5 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-dashed border-indigo-300 bg-white text-indigo-500 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
            >
              <Plus className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
      <span className={cn(compact ? "h-1" : "h-4", line)} />
      <ChevronDown className="-mt-1.5 h-3.5 w-3.5 text-slate-300" />
    </div>
  );
}

function FlowPill({ icon, tone, title, detail }: { icon: ReactNode; tone: string; title: string; detail: string }) {
  return (
    <div
      data-canvas-node
      tabIndex={0}
      className="flex items-center gap-2.5 rounded-full bg-white py-2 pl-2 pr-4 shadow-card-rest ring-1 ring-slate-900/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
    >
      <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white", tone)}>{icon}</span>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  );
}

/** Who entered the campaign: still Ready in Prism, or handed to Lemlist. */
function StartNode({ campaign }: { campaign: CampaignDetail }) {
  const counts = campaign.contacts_by_status;
  const total = Object.entries(counts).reduce((sum, [status, n]) => (status === "EMAIL_REMOVED" ? sum : sum + n), 0);
  const ready = counts.EMAIL_READY ?? 0;
  const parts = [ready ? `${ready} ready` : null, total - ready ? `${total - ready} handed to Lemlist` : null];
  return (
    <FlowPill
      icon={<Play className="h-3.5 w-3.5" />}
      tone="bg-emerald-500"
      title={campaign.execution_lock ? "Séquence en pause" : "Start"}
      detail={campaign.execution_lock ? (total ? `${total} prospects · aucun envoi` : "10 scénarios · aucun destinataire") : total ? [plural(total, "contact"), ...parts].filter(Boolean).join(" · ") : "No contact yet"}
    />
  );
}

/** Who went all the way, and how many left earlier. */
function EndNode({ campaign }: { campaign: CampaignDetail }) {
  const counts = campaign.contacts_by_status;
  const exits = [
    counts.EMAIL_REPLIED ? `${counts.EMAIL_REPLIED} replied` : null,
    counts.EMAIL_BOUNCED ? `${counts.EMAIL_BOUNCED} bounced` : null,
    counts.EMAIL_UNSUBSCRIBED ? `${counts.EMAIL_UNSUBSCRIBED} unsubscribed` : null,
  ].filter(Boolean);
  return (
    <FlowPill
      icon={<Flag className="h-3.5 w-3.5" />}
      tone="bg-slate-800"
      title={campaign.execution_lock ? "Fin de la séquence" : "End of sequence"}
      detail={campaign.execution_lock ? "Arrêt en cas de réponse" : `${plural(counts.EMAIL_COMPLETED ?? 0, "contact")} completed${exits.length ? ` · left earlier: ${exits.join(", ")}` : ""}`}
    />
  );
}

/** One email of the sequence, with what it did. */
function StepNode({
  step,
  day,
  props,
  metrics,
  atStep,
  move,
}: {
  step: CampaignStep;
  day: number;
  props: StepBoardProps;
  metrics: EmailMetrics | undefined;
  atStep: number;
  move?: { up: (() => void) | null; down: (() => void) | null };
}) {
  const pilot = !!props.campaign.execution_lock && !!props.campaign.pilot?.editorial;
  const editable = props.canEdit || pilot;
  const sent = metrics?.sent ?? 0;
  const numbers = metrics
    ? [
        `${sent} sent`,
        `${metrics.opened} opened`,
        metrics.clicked ? `${metrics.clicked} clicked` : null,
        `${metrics.replied} replied`,
        metrics.bounced ? `${metrics.bounced} bounced` : null,
      ].filter(Boolean)
    : [];
  return (
    <article
      data-canvas-node
      tabIndex={0}
      aria-label={`Step ${step.position}: ${step.name}`}
      onClick={() => editable && props.onEdit(step)}
      onKeyDown={(event) => {
        if (editable && event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          props.onEdit(step);
        }
      }}
      className={cn(
        "rounded-plane bg-white text-left shadow-card-rest ring-1 ring-slate-900/[0.04] outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-indigo-300",
        pilot ? "p-3" : "p-4",
        editable && "cursor-pointer hover:shadow-plane-lift",
      )}
    >
      <header className="flex items-start gap-2.5">
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
            step.position === 1 ? "bg-indigo-600 text-white shadow-cta" : "bg-white text-indigo-600 ring-1 ring-indigo-100",
          )}
        >
          {step.position}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-[15px] font-semibold text-slate-900">{pilot && props.campaign.pilot?.profile !== "CFO_US_GROWTH_V1" ? ["", "Premier contact", "Valeur et financement", "Situation comparable", "Dernier message"][step.position] : step.name}</h3>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              <Clock className="h-3 w-3" />
              {pilot ? (day ? `J+${day}` : "J0") : `Day ${day}`}
            </span>
          </div>
          {step.capabilities && (step.capabilities.added_after_launch || step.capabilities.pending) && (
            <div className="mt-1 flex flex-wrap gap-1">
              <StepBadges step={step} />
            </div>
          )}
          {!pilot && <div className="mt-1 truncate">
            <SubjectLine step={step} />
          </div>}
        </div>
        <StepMenu step={step} props={props} move={move} />
      </header>
      {pilot && step.position === 1 && <div className="mt-3 flex gap-2">{pilotVariantKeys(props.campaign.pilot!).map((variant) => <button key={variant} type="button" onClick={(event) => { event.stopPropagation(); props.onPilotVariant?.(variant); }} className="flex-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2 py-2 text-xs font-medium text-indigo-800">{`${pilotVariantLabel(props.campaign.pilot, variant)} · ${pilotPeople(props.campaign.pilot!).filter((p) => p.variant === variant).length} ${props.campaign.pilot?.kind === "PILOT" ? "prospects" : "scénarios"}`}</button>)}</div>}
      {!pilot && <><p className="mt-2 line-clamp-2 whitespace-pre-line text-[13px] leading-relaxed text-slate-600">
        <TemplateText template={step.body_template} empty="Empty body" />
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className={sent ? "font-medium text-slate-700" : "text-slate-400"}>
          {sent ? numbers.join(" · ") : "Nothing sent yet"}
        </span>
        {atStep > 0 && (
          <Hint content="Running contacts whose last email was this step">
            <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-700">
              <Users className="h-3 w-3" />
              {atStep} at this step
            </span>
          </Hint>
        )}
      </div>
      <div className="mt-3 border-t border-slate-100 pt-2.5">
        <StepFacts step={step} contacts={0} synced={props.campaign.provider_mode !== "NONE"} />
      </div></>}
    </article>
  );
}

/** The way out at each email: a reply stops the sequence for that contact. */
function ExitBranch({ metrics }: { metrics: EmailMetrics | undefined }) {
  const replied = metrics?.replied ?? 0;
  const bounced = metrics?.bounced ?? 0;
  return (
    <div className="flex items-center">
      <span className="w-6 shrink-0 border-t border-dashed border-slate-300" />
      <div className="flex flex-col items-start gap-1">
        <Hint content="A reply stops the sequence for that contact: no later step is sent.">
          <span
            data-canvas-node
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
          >
            <Reply className="h-3 w-3" />
            Reply → stops
            {replied > 0 && (
              <span className="ml-0.5 rounded-full bg-emerald-600 px-1.5 text-[10px] font-semibold text-white">{replied}</span>
            )}
          </span>
        </Hint>
        {bounced > 0 && (
          <span className="inline-flex items-center whitespace-nowrap rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
            {bounced} bounced
          </span>
        )}
      </div>
    </div>
  );
}
